/**
 * Grid Calculator Functions
 * 
 * Core algorithm for finding valid grid configurations
 */

/**
 * Find all valid sizes for one dimension that satisfy margin constraints
 * Optimized version that eliminates multiplications and divisions in the loop
 * 
 * @param {number} length - Page dimension (width or height)
 * @param {number} count - Number of elements (columns or rows) 
 * @param {number} gutterSize - Gutter size in grid units
 * @param {number} atomSize - Base grid size (line height)
 * @returns {Array<{size: number, margin: number}>} Valid sizes with their margins (sorted from tightest to largest margin)
 */
export function findValidSizes(length, count, gutterSize, atomSize) {
  const validSizes = [];
  const maxMargin = length * 0.25; // 25% constraint
  
  // Calculate the maximum possible size that could theoretically fit
  const minContentSize = count * atomSize + (count - 1) * gutterSize * atomSize;
  const maxPossibleSize = Math.floor((length - minContentSize) / (count * atomSize)) + 1;
  
  // Pre-calculate values outside loop to avoid operations inside loop
  const guttersSize = (count - 1) * gutterSize * atomSize; // Constant throughout loop
  const elementsSizeDelta = count * atomSize; // Amount elements size decreases each iteration
  const marginDelta = elementsSizeDelta / 2; // Amount margin increases each iteration
  
  // Start with elements size and margin for maximum possible size
  let elementsSize = count * maxPossibleSize * atomSize;
  let margin = (length - (elementsSize + guttersSize)) / 2; // Initial margin calculation
  
  // Search from maximum possible size down to 1 (tightest margin to largest)
  for (let size = maxPossibleSize; size >= 1; size--) {
    // Check if this size is valid - no calculations needed, just comparisons!
    // 1. Margin must be non-negative (content fits in page)
    // 2. Margin must not exceed 25% of page size
    if (margin >= 0 && margin <= maxMargin) {
      validSizes.push({ size, margin });
    }
    
    // Early break: as size decreases, margin increases monotonically
    // Once margin > maxMargin, all smaller sizes will also exceed the limit
    if (margin > maxMargin) {
      break;
    }
    
    // Prepare for next iteration: reduce elements size and increase margin
    // No multiplications or divisions needed!
    elementsSize -= elementsSizeDelta;
    margin += marginDelta;
  }
  
  return validSizes;
}

/**
 * Calculate all valid grid configurations by combining horizontal and vertical solutions
 * 
 * @param {Object} input - Grid parameters
 * @param {number} input.pageWidth - Page width
 * @param {number} input.pageHeight - Page height  
 * @param {number} input.lineHeight - Base grid size
 * @param {number} input.cols - Number of columns
 * @param {number} input.rows - Number of rows
 * @param {number} input.colGutter - Column gutter size in grid units
 * @param {number} input.rowGutter - Row gutter size in grid units
 * @returns {Array<Object>} Array of valid configurations
 */
export function calculateGridConfigurations(input) {
  // Input validation
  if (input.pageWidth <= 0 || input.pageHeight <= 0) {
    throw new Error('Invalid page dimensions');
  }
  
  if (input.lineHeight <= 0) {
    throw new Error('Invalid line height');
  }
  
  if (input.cols <= 0 || input.rows <= 0) {
    throw new Error('Invalid grid dimensions');
  }
  
  if (input.colGutter < 0 || input.rowGutter < 0) {
    throw new Error('Invalid gutter dimensions');
  }
  
  // Find valid sizes for each dimension
  const validColWidths = findValidSizes(
    input.pageWidth, 
    input.cols, 
    input.colGutter, 
    input.lineHeight
  );
  
  const validRowHeights = findValidSizes(
    input.pageHeight, 
    input.rows, 
    input.rowGutter, 
    input.lineHeight
  );
  
  // Create cartesian product of valid combinations
  const configurations = [];
  
  for (const colConfig of validColWidths) {
    for (const rowConfig of validRowHeights) {
      configurations.push({
        colWidth: colConfig.size,
        rowHeight: rowConfig.size,
        leftMargin: colConfig.margin,
        rightMargin: colConfig.margin,
        topMargin: rowConfig.margin,
        bottomMargin: rowConfig.margin
      });
    }
  }
  
  return configurations;
}